import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** When true, the textarea automatically grows/shrinks to fit its content */
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, autoResize, ...props }, ref) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Resize after every render when autoResize is on, using useLayoutEffect
  // so it happens before the browser paints (no flicker)
  React.useLayoutEffect(() => {
    if (!autoResize) return;
    const el = innerRef.current;
    if (!el) return;
    el.style.minHeight = '0';
    el.style.height = 'auto';
    el.style.minHeight = `${el.scrollHeight}px`;
  });

  return (
    <textarea
      className={cn(
        'flex w-full rounded-sm border border-input bg-background py-2 px-2 text-base sm:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-input-focus disabled:cursor-not-allowed disabled:opacity-50',
        !props.rows && !autoResize && 'min-h-[80px]',
        autoResize && 'resize-none overflow-hidden',
        className,
      )}
      ref={(el) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      {...props}
    />
  );
});
Textarea.displayName = 'Textarea';

export { Textarea };
